import { eventChannel, type EventChannel } from 'redux-saga';
import { call, put, take, takeLatest } from 'redux-saga/effects';
import type { Event } from '@nx/domain';
import {
  eventRepository,
  getOrganizerId,
  mediaRepository,
} from '../../services/eoRepositories';
import { pushToast } from '../slices/eoUiSlice';
import {
  eventCreateRequested,
  eventDeleteRequested,
  eventLoadRequested,
  eventLoaded,
  eventSaveSucceeded,
  eventsFailed,
  eventsLoaded,
  eventsWatchRequested,
  eventUpdateRequested,
} from '../slices/eventsSlice';

function createEventsChannel(organizerId: string): EventChannel<Event[] | Error> {
  return eventChannel<Event[] | Error>((emit) =>
    eventRepository.watchByOrganizer(
      organizerId,
      (events) => emit(events),
      (error) => emit(error),
    ),
  );
}

function* watchEvents(organizerId: string): Generator<unknown, void, unknown> {
  const channel = (yield call(createEventsChannel, organizerId)) as ReturnType<
    typeof createEventsChannel
  >;
  try {
    while (true) {
      const payload = (yield take(channel)) as Event[] | Error;
      if (payload instanceof Error) {
        yield put(eventsFailed(payload.message));
        break;
      }
      yield put(eventsLoaded(payload));
    }
  } finally {
    channel.close();
  }
}

export function* handleEventsWatch(
  action: ReturnType<typeof eventsWatchRequested>,
): Generator<unknown, void, unknown> {
  const organizerId = action.payload || getOrganizerId();
  if (!organizerId) return;
  yield call(watchEvents, organizerId);
}

export function* handleEventLoad(action: ReturnType<typeof eventLoadRequested>): Generator<unknown, void, unknown> {
  try {
    const event = (yield call([eventRepository, eventRepository.getById], action.payload)) as Event | null;
    yield put(eventLoaded(event));
  } catch (e) {
    yield put(eventsFailed(e instanceof Error ? e.message : 'Failed to load event'));
  }
}

function* handleEventCreate(
  action: ReturnType<typeof eventCreateRequested>,
): Generator<unknown, void, unknown> {
  try {
    const organizerId = getOrganizerId();
    if (!organizerId) throw new Error('Not signed in');
    let coverUrl = action.payload.input.coverImageUrl ?? null;
    const event = (yield call(
      [eventRepository, eventRepository.create],
      organizerId,
      { ...action.payload.input, coverImageUrl: coverUrl },
    )) as Event;
    if (action.payload.coverFile) {
      coverUrl = (yield call(
        [mediaRepository, mediaRepository.uploadEventCover],
        organizerId,
        event.id,
        action.payload.coverFile,
        action.payload.coverFile.name,
      )) as string;
      const updated = (yield call(
        [eventRepository, eventRepository.update],
        event.id,
        { coverImageUrl: coverUrl },
      )) as Event;
      yield put(eventSaveSucceeded(updated));
    } else {
      yield put(eventSaveSucceeded(event));
    }
    yield put(pushToast({ msg: 'eo.toast.eventCreated', kind: 'success' }));
  } catch (e) {
    yield put(eventsFailed(e instanceof Error ? e.message : 'Failed to create event'));
    yield put(pushToast({ msg: 'eo.toast.eventCreateFailed', kind: 'danger' }));
  }
}

function* handleEventUpdate(
  action: ReturnType<typeof eventUpdateRequested>,
): Generator<unknown, void, unknown> {
  try {
    const organizerId = getOrganizerId();
    let input = action.payload.input;
    if (action.payload.coverFile && organizerId) {
      const coverImageUrl = (yield call(
        [mediaRepository, mediaRepository.uploadEventCover],
        organizerId,
        action.payload.eventId,
        action.payload.coverFile,
        action.payload.coverFile.name,
      )) as string;
      input = { ...input, coverImageUrl };
    }
    const updated = (yield call(
      [eventRepository, eventRepository.update],
      action.payload.eventId,
      input,
    )) as Event;
    yield put(eventSaveSucceeded(updated));
    yield put(pushToast({ msg: 'eo.toast.eventSaved', kind: 'success' }));
  } catch (e) {
    yield put(eventsFailed(e instanceof Error ? e.message : 'Failed to save event'));
    yield put(pushToast({ msg: 'eo.toast.eventSaveFailed', kind: 'danger' }));
  }
}

function* handleEventDelete(
  action: ReturnType<typeof eventDeleteRequested>,
): Generator<unknown, void, unknown> {
  try {
    yield call([eventRepository, eventRepository.delete], action.payload);
    yield put(pushToast({ msg: 'eo.toast.eventDeleted', kind: 'success' }));
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.eventDeleteFailed', kind: 'danger' }));
  }
}

export function* eventsSaga() {
  yield takeLatest(eventsWatchRequested.type, handleEventsWatch);
  yield takeLatest(eventLoadRequested.type, handleEventLoad);
  yield takeLatest(eventCreateRequested.type, handleEventCreate);
  yield takeLatest(eventUpdateRequested.type, handleEventUpdate);
  yield takeLatest(eventDeleteRequested.type, handleEventDelete);
}
